import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, { LazyTableRow, TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { calculateSequence } from "helpers/arrayHelpers";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { MediaLibraryDialogView } from "pages/MediaLibraryPage/MediaLibraryDialogView";
import { useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  IMediaItem,
  ListRequestOfSkuDeliverableFilter,
  MediaItemSource,
  ProductDeliverableFilter,
  ProductDeliverableInfo,
  SaveSkuDeliverableRequest,
  SkuDeliverableFilter,
  SkuDeliverableInfo,
} from "services/KnowfullyAdminClient";
import {
  DeliverableTypeIds,
  DeliveryMethodSettingCodeConstants,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import { MediaType } from "utils/mediaUtils";
import HandoutOrderingNotAllowedDialog from "./HandoutOrderingNotAllowedDialog";
import HandoutUpdateDialog from "./HandoutUpdateDialog";

export const HANDOUT_MAX_FILE_SIZE = 250 * 1024 * 1024;

type Handout = ProductDeliverableInfo &
  SkuDeliverableInfo &
  LazyTableRow & { level: "product" | "sku" };

type SkuHandoutsProps = { isReadonly?: boolean } & SkuFormProps;

const SkuHandouts = ({ isReadonly, sku }: SkuHandoutsProps) => {
  const { formatMessage: t } = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const {
    productService,
    productDeliverableService,
    skuDeliverableService,
  } = useGlobalContext();
  const [deleting, setDeleting] = useState<Handout>();
  const [editing, setEditing] = useState<Handout>();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createDialogOpen, createDialogOpenActions] = useBoolean(false);
  const [cantDropDialogOpen, cantDropDialogOpenActions] = useBoolean(false);

  const {
    data: skuHandoutsData,
    isFetching: isFetchingSkuHandouts,
  } = useStrictQuery(
    ["getSkuDeliverables", sku.id],
    () =>
      skuDeliverableService.getSkuDeliverables(
        sku.id!,
        ListRequestOfSkuDeliverableFilter.fromJS({
          sortField: "sequence",
          parameters: SkuDeliverableFilter.fromJS({
            deliverableTypeIds: [DeliverableTypeIds.Handout],
          }),
        })
      ),
    {
      enabled: !!sku.id,
    }
  );

  const {
    data: productHandoutsData,
    isFetching: isFetchingProductHandouts,
  } = useStrictQuery(
    ["getProductDeliverables", sku.productId],
    () =>
      productDeliverableService.getProductDeliverables(
        sku.productId!,
        ListRequestOfSkuDeliverableFilter.fromJS({
          sortField: "sequence",
          parameters: ProductDeliverableFilter.fromJS({
            deliverableTypeIds: [DeliverableTypeIds.Handout],
          }),
        })
      ),
    {
      enabled: !!sku.productId,
    }
  );

  const { data: product } = useStrictQuery(["product", sku.productId], () =>
    productService.getProductById(sku.productId!)
  );

  const invalidateQueries = () => {
    queryClient.invalidateQueries(["getProductDeliverables", sku.productId]);
    queryClient.invalidateQueries(["getSkuDeliverables", sku.id]);
  };

  const ActionsCell = ({ value }: { value: Handout }) => {
    return (
      <Box display="flex" justifyContent="right">
        <IconButton
          size="small"
          color="success"
          onClick={() =>
            window.open(
              value.settingValues?.find(
                (v) =>
                  v.setting?.codeConstant ===
                  DeliveryMethodSettingCodeConstants.Url
              )?.value
            )
          }
        >
          <LinkIcon />
        </IconButton>
        {!isReadonly && (
          <>
            <IconButton
              size="small"
              onClick={() => setEditing(value)}
              disabled={value.level === "product"}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setDeleting(value)}
              disabled={value.level === "product"}
            >
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </Box>
    );
  };

  const handleDelete = async () => {
    if (deleting?.skuDeliverableId) {
      await skuDeliverableService.deleteSkuDeliverable(
        deleting.skuDeliverableId
      );
      invalidateQueries();
      setDeleting(undefined);
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "handoutDeleted" }), Severity.Warning)
      );
    }
  };

  const productHandouts = (productHandoutsData?.data ?? []).map(
    (h) =>
      ({
        id: h.id!,
        color: theme.palette.grey[200],
        isDragDisabled: true,
        level: "product",
        tooltip: product?.data?.name
          ? t(
              { id: "handoutInheritedFromProduct" },
              { productName: product.data.name }
            )
          : undefined,
        ...h,
      } as Handout)
  );
  const skuHandouts = (skuHandoutsData?.data ?? []).map(
    (h) =>
      ({
        id: h.skuDeliverableId!,
        level: "sku",
        ...h,
      } as Handout)
  );

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index - productHandouts.length;
    const destinationIndex = result.destination.index - productHandouts.length;

    if (destinationIndex < 0) {
      cantDropDialogOpenActions.setTrue();
      invalidateQueries();
      return;
    }

    const idSequenceMap = calculateSequence(
      skuHandouts,
      sourceIndex,
      destinationIndex
    );

    await skuDeliverableService.updateSkuDeliverableSequence(
      sku.id!,
      idSequenceMap
    );

    invalidateQueries();
  };

  const handleSave = async (libraryItem: IMediaItem) => {
    await skuDeliverableService.saveSkuDeliverable(
      SaveSkuDeliverableRequest.fromJS({
        skuId: sku.id,
        deliverableId: libraryItem.id,
        sequence: skuHandouts.length
          ? Math.max(...skuHandouts.map((h) => h.sequence ?? 0)) + 1
          : 0,
      })
    );

    invalidateQueries();
    createDialogOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "handoutAdded" }), Severity.Success)
    );
  };

  const onHandoutUpdated = () => {
    queryClient.invalidateQueries("getSkuDeliverables");

    setEditing(undefined);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "handoutUpdated" }), Severity.Success)
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "displayText",
      type: "text",
      label: t({ id: "displayText" }),
      noSort: true,
    },
    {
      accessor: "description",
      type: "text",
      label: t({ id: "descriptionLabel" }),
      noSort: true,
    },
    {
      accessor: "",
      type: "node",
      label: t({ id: "fileNameLabel" }),
      noSort: true,
      View: ({ value }: { value: Handout }) => (
        <>
          {
            value.settingValues?.find(
              (v) =>
                v.setting?.codeConstant ===
                DeliveryMethodSettingCodeConstants.FileName
            )?.value
          }
        </>
      ),
    },
    {
      accessor: "",
      width: 1,
      type: "node",
      View: ActionsCell,
      label: "",
      noSort: true,
    },
  ];

  if (!isReadonly) {
    cells.unshift({
      accessor: "",
      width: 1,
      type: "node",
      View: ({ value }) => (
        <DragHandleIcon color={value.isDragDisabled ? "disabled" : undefined} />
      ),
      label: "",
      noSort: true,
    });
  }

  const allHandouts = productHandouts.concat(skuHandouts);

  return (
    <Container>
      {!isReadonly && (
        <>
          <Box my={2} textAlign="right">
            <Button
              variant="outlined"
              type="submit"
              color="primary"
              onClick={createDialogOpenActions.setTrue}
            >
              <T id="addHandout" />
            </Button>
          </Box>

          <DeleteEntityDialog
            dialogTitleKey="deleteHandoutFromSku"
            contentKey="areYouSureToDeleteHandout"
            onCancel={() => setDeleting(undefined)}
            handleDelete={handleDelete}
            open={!!deleting}
          />

          <Dialog
            fullWidth
            maxWidth={"lg"}
            open={createDialogOpen}
            onClose={createDialogOpenActions.setFalse}
          >
            <DialogContent>
              <MediaLibraryDialogView
                source={MediaItemSource.Handouts}
                onClickMediaLibraryItem={handleSave}
                options={[MediaType.DOCUMENT, MediaType.IMAGE, MediaType.ARCHIVE]}
                customBackButtonLabel="backToCourseMaterialsLibrary"
                alternativeTextRequired={false}
                customAlternativeTextLabel="displayText"
                maxFileSizeBytes={HANDOUT_MAX_FILE_SIZE}
              />
            </DialogContent>
          </Dialog>

          <HandoutUpdateDialog
            handoutId={editing?.deliverableId}
            onUpdated={onHandoutUpdated}
            onClose={() => setEditing(undefined)}
          />

          <SnackbarMessage
            snackbarMessage={snackbarMessage}
            setSnackbarMessage={setSnackbarMessage}
          />

          <HandoutOrderingNotAllowedDialog
            open={cantDropDialogOpen}
            onClose={cantDropDialogOpenActions.setFalse}
          />
        </>
      )}

      <LazyTable
        {...{
          cells,
          rows: allHandouts,
          rowCount: allHandouts.length,
          page: 0,
          setPage: () => {},
          rowsPerPage: allHandouts.length,
          setRowsPerPage: () => {},
          order: "asc",
          setOrder: () => {},
          orderBy: "",
          setOrderBy: () => {},
          loading: isFetchingSkuHandouts || isFetchingProductHandouts,
          dense: true,
          allowDragAndDrop: !isReadonly,
          onDragAndDropEnd: handleDragEnd,
          allowPagination: false,
        }}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuHandouts);
